// src/pages/coming-soon.tsx
//
// Reusable placeholder page for product modules that are routed but not
// yet built (the asset-ranking suite and the portfolio optimizer). It
// reuses the standard <Layout sidebar={<Sidebar />}> shell so the route
// feels like a real in-app destination instead of bouncing the user to
// the public landing page via the catch-all route.
//
// Each not-yet-built /dashboard/* route in App.tsx renders this with a
// module-specific title + blurb. Delete the route's <ComingSoon /> and
// swap in the real feature page once the module ships.

import { Link } from 'react-router-dom';

import { Layout, Sidebar } from '../shared/ui';

interface ComingSoonProps {
    /** Module name shown as the page title, e.g. "Equities Ranking". */
    title: string;
    /** One-line description of what the module will do once shipped. */
    description: string;
}

export default function ComingSoon({ title, description }: ComingSoonProps) {
    return (
        <Layout
            sidebar={<Sidebar />}
            title={title}
            subtitle="This module is in active development."
        >
            <section
                aria-label={`${title} — coming soon`}
                className="surface-card-interactive flex flex-col items-center justify-center py-16 text-center"
            >
                <div className="h-14 w-14 rounded-full bg-green-100 dark:bg-emerald-900/50 text-green-600 dark:text-emerald-400 flex items-center justify-center mb-5">
                    {/* Wrench / build icon */}
                    <svg
                        width="26"
                        height="26"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                    </svg>
                </div>
                <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-emerald-900/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-emerald-300">
                    Coming Soon
                </span>
                <h2 className="section-title mt-4">{title}</h2>
                <p className="muted mt-2 max-w-md text-sm">{description}</p>
                <Link
                    to="/dashboard"
                    className="mt-8 inline-flex items-center gap-2 rounded-xl bg-green-600 dark:bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700 dark:hover:bg-emerald-500"
                >
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                    </svg>
                    Back to Command Center
                </Link>
            </section>
        </Layout>
    );
}
